import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { FaMobileAlt, FaExclamationTriangle } from 'react-icons/fa'
import { mobileConfigService } from '../services/mobileConfigService'

const MobileAppConfigPage = () => {
  const [configs, setConfigs] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedPlatform, setSelectedPlatform] = useState('android_client')
  const [formData, setFormData] = useState({
    minSupportedVersion: '',
    latestVersion: '',
    forceUpdate: false,
    storeUrl: '',
  })
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingFormData, setPendingFormData] = useState(null)

  useEffect(() => { fetchConfigs() }, [])

  useEffect(() => {
    if (configs.length > 0) {
      const config = configs.find(c => c.platform === selectedPlatform)
      if (config) {
        setFormData({
          minSupportedVersion: config.minSupportedVersion || '',
          latestVersion: config.latestVersion || '',
          forceUpdate: config.forceUpdate || false,
          storeUrl: config.storeUrl || '',
        })
        setErrors({})
      }
    }
  }, [selectedPlatform, configs])

  useEffect(() => {
    if (configs.length > 0 && !configs.find((c) => c.platform === selectedPlatform)) {
      setSelectedPlatform(configs[0].platform)
    }
  }, [configs, selectedPlatform])

  const fetchConfigs = async () => {
    setLoading(true)
    try {
      const result = await mobileConfigService.getAllConfigs()
      if (result.success) {
        setConfigs(result.data || [])
        if (result.data && result.data.length > 0 && !configs.find(c => c.platform === selectedPlatform)) {
          setSelectedPlatform(result.data[0].platform)
        }
      } else {
        toast.error(result.message || 'Failed to load configurations')
      }
    } catch (error) {
      toast.error('Failed to load mobile app configurations')
    } finally {
      setLoading(false)
    }
  }

  const validateVersion = (version) => {
    if (!version || typeof version !== 'string') return false
    return /^\d+\.\d+\.\d+$/.test(version.trim())
  }

  const compareVersions = (v1, v2) => {
    const parts1 = v1.split('.').map(Number)
    const parts2 = v2.split('.').map(Number)
    while (parts1.length < 3) parts1.push(0)
    while (parts2.length < 3) parts2.push(0)
    for (let i = 0; i < 3; i++) {
      if (parts1[i] < parts2[i]) return -1
      if (parts1[i] > parts2[i]) return 1
    }
    return 0
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.minSupportedVersion.trim()) newErrors.minSupportedVersion = 'Minimum supported version is required'
    else if (!validateVersion(formData.minSupportedVersion)) newErrors.minSupportedVersion = 'Invalid format. Must be semantic version (x.y.z)'
    if (!formData.latestVersion.trim()) newErrors.latestVersion = 'Latest version is required'
    else if (!validateVersion(formData.latestVersion)) newErrors.latestVersion = 'Invalid format. Must be semantic version (x.y.z)'
    if (!formData.storeUrl.trim()) newErrors.storeUrl = 'Store URL is required'
    else { try { new URL(formData.storeUrl) } catch { newErrors.storeUrl = 'Invalid URL format' } }
    if (!newErrors.minSupportedVersion && !newErrors.latestVersion &&
      compareVersions(formData.minSupportedVersion, formData.latestVersion) > 0) {
      newErrors.latestVersion = 'Latest version must be ≥ minimum supported version'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return
    if (formData.forceUpdate) { setPendingFormData({ ...formData }); setShowConfirmDialog(true); return }
    await saveConfig()
  }

  const saveConfig = async () => {
    setSaving(true)
    try {
      const updateData = {
        minSupportedVersion: formData.minSupportedVersion.trim(),
        latestVersion: formData.latestVersion.trim(),
        forceUpdate: formData.forceUpdate,
        storeUrl: formData.storeUrl.trim(),
      }
      const result = await mobileConfigService.updateConfig(selectedPlatform, updateData)
      if (result.success) {
        toast.success(result.message || 'Configuration saved successfully')
        await fetchConfigs()
        setShowConfirmDialog(false)
      } else {
        toast.error(result.message || 'Failed to save configuration')
      }
    } catch (error) {
      toast.error('Failed to save mobile app configuration')
    } finally {
      setSaving(false)
    }
  }

  const platformLabel = (p) =>
    p === 'android_client' ? 'Android Client' : p === 'android_driver' ? 'Android Driver' : 'iOS'

  const currentConfig = configs.find(c => c.platform === selectedPlatform)

  const inputClass = (hasError) =>
    `w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${
      hasError ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'
    }`

  if (loading) {
    return (
      <div className="px-[20px] md:px-[40px] py-[30px] min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#3498db] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#7f8c8d]">Loading configurations…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="px-[20px] md:px-[40px] py-[30px] min-h-screen bg-[#f8f9fa]">
      <div className="pb-5 mb-8 border-b border-[#e9ecef]">
        <div className="flex items-center gap-3">
          <FaMobileAlt className="text-[#3498db] text-xl" />
          <h1 className="text-[1.8rem] font-semibold text-[#2c3e50]">Mobile App Config</h1>
        </div>
        <p className="text-sm text-[#7f8c8d] mt-1">Manage version requirements and update policies for mobile apps</p>
      </div>

      <div className="max-w-2xl">
        {/* Platform tabs */}
        <div className="flex gap-2 mb-6">
          {['android_client', 'android_driver', 'ios'].map((platform) => (
            <button key={platform} type="button" onClick={() => setSelectedPlatform(platform)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                selectedPlatform === platform
                  ? 'bg-[#2c3e50] text-white border-[#2c3e50]'
                  : 'bg-white text-[#7f8c8d] border-[#e9ecef] hover:border-[#2c3e50] hover:text-[#2c3e50]'
              }`}>
              {platformLabel(platform)}
            </button>
          ))}
        </div>

        <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm p-6">
          {/* Force update status badge */}
          {currentConfig && (
            <div className="mb-6">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                currentConfig.forceUpdate
                  ? 'bg-[#fdedec] text-[#e74c3c]'
                  : 'bg-[#eafaf1] text-[#27ae60]'
              }`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                {currentConfig.forceUpdate ? 'Force Update Active' : 'Soft Update'}
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">
                  Min Supported Version <span className="text-[#e74c3c]">*</span>
                </label>
                <input type="text" value={formData.minSupportedVersion}
                  onChange={(e) => handleInputChange('minSupportedVersion', e.target.value)}
                  className={inputClass(errors.minSupportedVersion)} placeholder="e.g., 1.1.3" />
                {errors.minSupportedVersion
                  ? <p className="text-[#e74c3c] text-xs mt-1">{errors.minSupportedVersion}</p>
                  : <p className="text-[#7f8c8d] text-xs mt-1">Users below this version will be blocked</p>
                }
              </div>

              <div>
                <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">
                  Latest Version <span className="text-[#e74c3c]">*</span>
                </label>
                <input type="text" value={formData.latestVersion}
                  onChange={(e) => handleInputChange('latestVersion', e.target.value)}
                  className={inputClass(errors.latestVersion)} placeholder="e.g., 1.2.0" />
                {errors.latestVersion
                  ? <p className="text-[#e74c3c] text-xs mt-1">{errors.latestVersion}</p>
                  : <p className="text-[#7f8c8d] text-xs mt-1">Current latest version available</p>
                }
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">
                Store URL <span className="text-[#e74c3c]">*</span>
              </label>
              <input type="url" value={formData.storeUrl}
                onChange={(e) => handleInputChange('storeUrl', e.target.value)}
                className={inputClass(errors.storeUrl)} placeholder="https://play.google.com/store/apps/..." />
              {errors.storeUrl && <p className="text-[#e74c3c] text-xs mt-1">{errors.storeUrl}</p>}
            </div>

            {/* Force update toggle */}
            <div className="pt-4 border-t border-[#e9ecef]">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative" onClick={() => handleInputChange('forceUpdate', !formData.forceUpdate)}>
                  <div className={`w-11 h-6 rounded-full transition-colors ${formData.forceUpdate ? 'bg-[#e74c3c]' : 'bg-[#e9ecef]'}`} />
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.forceUpdate ? 'translate-x-6' : 'translate-x-1'}`} />
                </div>
                <div>
                  <p className={`text-sm font-medium ${formData.forceUpdate ? 'text-[#e74c3c]' : 'text-[#2c3e50]'}`}>
                    Force Update
                  </p>
                  <p className="text-xs text-[#7f8c8d]">Block users below the minimum supported version</p>
                </div>
              </label>
            </div>

            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving}
                className="bg-[#3498db] hover:bg-[#2980b9] text-white font-semibold px-6 py-2.5 rounded-lg transition-colors disabled:opacity-50">
                {saving ? 'Saving…' : 'Save Configuration'}
              </button>
              <button type="button" onClick={() => fetchConfigs()}
                className="text-[#7f8c8d] hover:text-[#2c3e50] px-6 py-2.5 rounded-lg border border-[#e9ecef] transition-colors">
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Confirm force update dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-5 border-b border-[#e9ecef] flex items-center gap-3">
              <FaExclamationTriangle className="text-[#e74c3c] text-lg" />
              <h3 className="text-base font-semibold text-[#2c3e50]">Confirm Force Update</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-[#7f8c8d]">
                For <strong className="text-[#2c3e50]">{platformLabel(selectedPlatform)}</strong> platform:{' '}
                users below version <strong className="text-[#e74c3c]">{pendingFormData?.minSupportedVersion}</strong> will be
                blocked immediately. Are you sure you want to proceed?
              </p>
            </div>
            <div className="px-5 pb-5 flex justify-end gap-3">
              <button type="button" onClick={() => { setShowConfirmDialog(false); setPendingFormData(null) }}
                className="px-4 py-2 text-sm text-[#7f8c8d] border border-[#e9ecef] rounded-lg hover:text-[#2c3e50] transition-colors">
                Cancel
              </button>
              <button type="button" onClick={() => { setShowConfirmDialog(false); saveConfig() }}
                className="px-4 py-2 text-sm bg-[#e74c3c] hover:bg-[#c0392b] text-white rounded-lg transition-colors">
                Yes, Force Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MobileAppConfigPage
